import React, { useEffect, useState } from 'react';
import { getProgress, ProgressResponse, ProgressDomain, Trend } from '../api/progress';
import { getEngagement, EngagementData } from '../api/placement';

// ─────────────────────────────────────────────────────────────────────────────
// Progress view — headline stats, per-domain strengths/weaknesses, and a simple
// over-time score chart. Role-aware copy: parent accounts ("child") read as
// "<Name>'s progress"; self accounts read as "Your progress".
// Styling reuses the dashboard's .cvr-card + brand CSS variables (no new deps;
// the over-time chart is a lightweight inline SVG).
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  studentId: string;
  /** Per-skill "practise this" from the skill-trend list (ROO-166). */
  onPractiseSkill?: (skill: string) => void;
}

const TREND_META: Record<Trend, { label: string; icon: string; color: string }> = {
  improving: { label: 'Improving', icon: '↗', color: 'var(--green)' },
  steady: { label: 'Steady', icon: '→', color: 'var(--anchor)' },
  declining: { label: 'Needs a push', icon: '↘', color: 'var(--coral)' },
  none: { label: 'Just getting started', icon: '•', color: 'var(--mute)' },
};

function statusColor(status: ProgressDomain['status']): string {
  if (status === 'strength') return 'var(--green)';
  if (status === 'needs_work') return 'var(--coral)';
  return 'var(--orange)';
}

function statusLabel(status: ProgressDomain['status']): string {
  if (status === 'strength') return 'Strength';
  if (status === 'needs_work') return 'Needs work';
  return 'Steady';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/** Date + time — several attempts can land on one day, so chips need the time
 *  to stay distinguishable (ROO-186). */
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// Lightweight inline SVG line chart for the over-time score series.
const ScoreChart: React.FC<{ points: { score: number; date: string }[] }> = ({ points }) => {
  const W = 640;
  const H = 200;
  const PAD = 28;
  if (points.length === 0) return null;

  const n = points.length;
  const x = (i: number) =>
    n === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (n - 1);
  const y = (score: number) => H - PAD - (score / 100) * (H - PAD * 2);

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.score).toFixed(1)}`)
    .join(' ');
  const areaPath =
    `${linePath} L ${x(n - 1).toFixed(1)} ${H - PAD} L ${x(0).toFixed(1)} ${H - PAD} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="Score over time"
      style={{ display: 'block' }}
    >
      {/* gridlines at 0/50/100 */}
      {[0, 50, 100].map((g) => (
        <g key={g}>
          <line
            x1={PAD}
            x2={W - PAD}
            y1={y(g)}
            y2={y(g)}
            stroke="var(--line)"
            strokeWidth={1}
          />
          <text x={4} y={y(g) + 4} fontSize={11} fill="var(--mute)">
            {g}
          </text>
        </g>
      ))}
      <path d={areaPath} fill="var(--green-soft)" opacity={0.6} />
      <path d={linePath} fill="none" stroke="var(--anchor)" strokeWidth={2.5} strokeLinejoin="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(p.score)} r={4.5} fill="var(--anchor)" />
          <title>{`${formatDate(p.date)}: ${p.score}%`}</title>
        </g>
      ))}
    </svg>
  );
};

const StatTile: React.FC<{ value: React.ReactNode; label: string; accent?: string }> = ({
  value,
  label,
  accent,
}) => (
  <div
    style={{
      background: 'var(--cream)',
      border: '1.5px solid var(--line)',
      borderRadius: 18,
      padding: '18px 20px',
      flex: '1 1 140px',
      minWidth: 140,
    }}
  >
    <div style={{ fontSize: 30, fontWeight: 800, color: accent || 'var(--anchor)', lineHeight: 1.1 }}>
      {value}
    </div>
    <div style={{ fontSize: 13, color: 'var(--mute)', marginTop: 6, fontWeight: 600 }}>{label}</div>
  </div>
);

const TREND_ARROW: Record<'up' | 'down' | 'flat' | 'new' | 'idle', { icon: string; color: string; label: string }> = {
  up: { icon: '↗', color: 'var(--green)', label: 'Improving' },
  down: { icon: '↘', color: 'var(--coral)', label: 'Slipping' },
  flat: { icon: '→', color: 'var(--mute)', label: 'Steady' },
  new: { icon: '✦', color: 'var(--anchor)', label: 'Just started' },
  idle: { icon: '·', color: 'var(--mute)', label: 'Not practised lately' },
};

const ProgressView: React.FC<Props> = ({ studentId, onPractiseSkill }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ProgressResponse | null>(null);
  const [engagement, setEngagement] = useState<EngagementData | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEngagement(studentId).then((e) => { if (!cancelled && e) setEngagement(e); }).catch(() => {});
    return () => { cancelled = true; };
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProgress(studentId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        console.error('Failed to load progress:', err);
        if (!cancelled) setError('Could not load progress right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // Role-aware framing.
  const possessive = data?.viewer?.possessive || 'your';
  const titlePossessive = possessive.charAt(0).toUpperCase() + possessive.slice(1);
  const isParent = data?.viewer?.audience === 'parent';
  const subjectName = data?.viewer?.subjectName;

  if (loading) {
    return (
      <section className="cvr-card" style={{ marginTop: 24 }}>
        <p style={{ color: 'var(--mute)', margin: 0 }}>Loading progress…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="cvr-card" style={{ marginTop: 24 }}>
        <p style={{ color: 'var(--coral)', margin: 0 }}>{error}</p>
      </section>
    );
  }

  // Empty state — no completed attempts.
  if (!data || !data.hasData) {
    return (
      <section className="cvr-card" style={{ marginTop: 24, textAlign: 'center' }}>
        <h2 style={{ fontSize: 22, color: 'var(--ink)', margin: '8px 0' }}>
          {titlePossessive} progress
        </h2>
        <p style={{ color: 'var(--mute)', maxWidth: 420, margin: '0 auto' }}>
          {isParent && subjectName
            ? `Take ${subjectName}'s first test to start seeing progress here.`
            : 'Take your first test to see progress here.'}
        </p>
      </section>
    );
  }

  const { summary, domains, strengths, weaknesses, timeSeries } = data;
  const assessments = data.assessments || [];
  const weeklyPractice = data.weeklyPractice || [];
  const skillTrends = data.skillTrends || [];
  const trendMeta = TREND_META[summary.trend];
  const latestAssessment = assessments[assessments.length - 1];
  const maxWeekly = Math.max(1, ...weeklyPractice.map((w) => w.answered));

  return (
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Effort-level ladder (ROO-172 A) */}
      {engagement?.level && (
        <section className="cvr-card">
          <div className="cvr-card__head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <h2>Your level</h2>
            <span style={{ fontSize: 13, color: 'var(--mute)', fontWeight: 600 }}>
              {engagement.level.answered.toLocaleString()} questions answered
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 34 }} aria-hidden="true">{engagement.level.icon}</span>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--ink)' }}>{engagement.level.name}</div>
              {engagement.level.next ? (
                <>
                  <div style={{ background: 'var(--line)', borderRadius: 999, height: 10, overflow: 'hidden', margin: '6px 0 4px' }}>
                    <div style={{ width: `${engagement.level.progressPct}%`, height: '100%', background: 'var(--green)', borderRadius: 999 }} />
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--mute)' }}>
                    {(engagement.level.next.threshold - engagement.level.answered).toLocaleString()} questions to {engagement.level.next.icon} {engagement.level.next.name}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--mute)' }}>Top of the ladder — legendary!</div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Assessment history — score/band across placement attempts (ROO-166) */}
      {assessments.length > 0 && (
        <section className="cvr-card">
          <div className="cvr-card__head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <h2>Assessment history</h2>
            {latestAssessment && (
              <span style={{ fontSize: 13, color: 'var(--mute)', fontWeight: 600 }}>
                Latest: {latestAssessment.scorePercent}% · Band {latestAssessment.band} ·{' '}
                {formatDate(latestAssessment.at)}
              </span>
            )}
          </div>
          {assessments.length < 2 ? (
            <p style={{ color: 'var(--mute)', margin: 0 }}>
              Take another assessment to see {possessive} improvement curve.
            </p>
          ) : (
            <>
              <ScoreChart points={assessments.map((a) => ({ score: a.scorePercent, date: a.at }))} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {assessments.map((a) => (
                  <span
                    key={a.id}
                    style={{
                      background: 'var(--green-soft)',
                      color: 'var(--anchor)',
                      borderRadius: 999,
                      padding: '4px 12px',
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    {formatDateTime(a.at)} · {a.scorePercent}% · Band {a.band}
                  </span>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Skill trends — recent 14 days vs before, weakest first (ROO-166) */}
      {skillTrends.length > 0 && (
        <section className="cvr-card">
          <div className="cvr-card__head">
            <h2>Skill trends</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {skillTrends.slice(0, 10).map((s) => {
              const meta = TREND_ARROW[s.trend] || TREND_ARROW.flat;
              return (
                <div key={s.skill} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ flex: '1 1 160px', fontWeight: 600, color: 'var(--ink)' }}>{s.label}</span>
                  <span style={{ color: meta.color, fontWeight: 700, fontSize: 13, minWidth: 96 }}>
                    {meta.icon} {meta.label}
                  </span>
                  <div style={{ flex: '2 1 180px', background: 'var(--line)', borderRadius: 999, height: 10, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${s.accuracy}%`,
                        height: '100%',
                        background: s.accuracy >= 80 ? 'var(--green)' : s.accuracy >= 50 ? 'var(--orange)' : 'var(--coral)',
                        borderRadius: 999,
                      }}
                    />
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--ink)', minWidth: 44, textAlign: 'right' }}>
                    {s.accuracy}%
                  </span>
                  {onPractiseSkill && (
                    <button
                      type="button"
                      onClick={() => onPractiseSkill(s.skill)}
                      style={{
                        border: '1.5px solid var(--green)',
                        background: 'var(--green-soft)',
                        color: 'var(--anchor)',
                        borderRadius: 999,
                        padding: '3px 12px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Practise →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Weekly practice volume (ROO-166) */}
      {weeklyPractice.length > 0 && (
        <section className="cvr-card">
          <div className="cvr-card__head">
            <h2>Practice this term</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 120 }}>
            {weeklyPractice.map((w) => (
              <div key={w.weekStart} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 40 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{w.answered}</span>
                <div
                  style={{
                    width: '70%',
                    maxWidth: 44,
                    height: `${Math.max(6, (w.answered / maxWeekly) * 80)}px`,
                    background: 'var(--green)',
                    borderRadius: '8px 8px 0 0',
                    opacity: 0.85,
                  }}
                  title={`${w.correct}/${w.answered} correct`}
                />
                <span style={{ fontSize: 11, color: 'var(--mute)' }}>
                  {new Date(w.weekStart).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Headline */}
      <section className="cvr-card">
        <div className="cvr-card__head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <h2>{titlePossessive} progress</h2>
          <span style={{ fontSize: 13, color: 'var(--mute)', fontWeight: 600 }}>
            From completed tests &amp; mock exams
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          <StatTile value={summary.testsCompleted} label="Tests completed" />
          <StatTile
            value={`${summary.overallAccuracy}%`}
            label="Test accuracy"
            accent="var(--orange)"
          />
          <StatTile value={summary.questionsAnswered} label="Test questions answered" />
          <StatTile
            value={
              <span style={{ color: trendMeta.color }}>
                {trendMeta.icon} {trendMeta.label}
              </span>
            }
            label="Recent trend"
            accent={trendMeta.color}
          />
        </div>
        <p style={{ fontSize: 13, color: 'var(--mute)', marginTop: 16, marginBottom: 0 }}>
          Last activity: {formatDate(summary.lastActivityAt)}
        </p>
      </section>

      {/* Strengths / weaknesses summary */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <section className="cvr-card">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>
                Strengths
              </div>
              {strengths.length === 0 ? (
                <span style={{ color: 'var(--mute)', fontSize: 14 }}>
                  Keep practising to build a strength.
                </span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {strengths.map((s) => (
                    <span
                      key={s}
                      style={{
                        background: 'var(--green-soft)',
                        color: 'var(--anchor)',
                        borderRadius: 999,
                        padding: '6px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ fontWeight: 700, color: 'var(--coral)', marginBottom: 8 }}>
                Needs work
              </div>
              {weaknesses.length === 0 ? (
                <span style={{ color: 'var(--mute)', fontSize: 14 }}>
                  Nothing flagged — nice work!
                </span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {weaknesses.map((w) => (
                    <span
                      key={w}
                      style={{
                        background: 'var(--coral-soft)',
                        color: 'var(--coral)',
                        borderRadius: 999,
                        padding: '6px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {w}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Per-domain breakdown bars */}
      {domains.length > 0 && (
      <section className="cvr-card">
        <div className="cvr-card__head">
          <h2>By subject</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {domains.map((d) => (
            <div key={d.domain}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 6,
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                  {d.label}
                  <span style={{ color: 'var(--mute)', fontWeight: 400, fontSize: 13 }}>
                    {' '}
                    · {d.attempts} {d.attempts === 1 ? 'test' : 'tests'}
                  </span>
                </span>
                <span style={{ fontWeight: 700, color: statusColor(d.status) }}>
                  {d.accuracy}%
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '.04em',
                    }}
                  >
                    {statusLabel(d.status)}
                  </span>
                </span>
              </div>
              <div
                style={{
                  background: 'var(--line)',
                  borderRadius: 999,
                  height: 12,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${d.accuracy}%`,
                    height: '100%',
                    background: statusColor(d.status),
                    borderRadius: 999,
                    transition: 'width .4s ease',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Over-time score chart (teacher-assigned / practice tests) */}
      {timeSeries.length > 0 && (
        <section className="cvr-card">
          <div className="cvr-card__head">
            <h2>Test scores over time</h2>
          </div>
          {timeSeries.length < 2 ? (
            <p style={{ color: 'var(--mute)', margin: 0 }}>
              Complete a few more tests to see a trend line.
            </p>
          ) : (
            <ScoreChart points={timeSeries.map((p) => ({ score: p.score, date: p.date }))} />
          )}
        </section>
      )}
    </div>
  );
};

export default ProgressView;
